/**
 * ChurchesManagement - Admin component for managing churches/assemblies
 */
import React, { useState, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { churchesAPI, getErrorMessage } from '@/lib/api';
import { useAuthStore } from '@/lib/store';
import api from '@/lib/api';
import PhoneInput from '@/components/PhoneInput';
import { BulkImportChurchesDialog } from '@/components/church/BulkImportChurchesDialog';
import { OrganisationTree } from '@/components/church/OrganisationTree';
import { OrganisationFormDialog } from '@/components/church/OrganisationFormDialog';
import { OrganisationWizard } from '@/components/church/OrganisationWizard';
import { OrganisationSetupDialog } from '@/components/church/OrganisationSetupDialog';
import { LeadershipDirectory } from '@/components/church/LeadershipDirectory';
import { ClergyTransfers } from '@/components/church/ClergyTransfers';
import { DirectoryAuditPanel } from '@/components/church/DirectoryAuditPanel';
import { CountryCleanupPanel } from '@/components/church/CountryCleanupPanel';
import { StaleRecordsPanel } from '@/components/church/StaleRecordsPanel';
import { toast } from 'sonner';
import { uploadMedia } from '@/lib/mediaUpload';

// Legacy `level` values → canonical organisation_type (mirrors
// backend services/organisations.py LEVEL_TO_TYPE).
const LEVEL_TO_ORG_TYPE = {
  hq: 'global_ministry',
  global_ministry: 'global_ministry',
  country: 'country',
  province: 'province',
  region: 'province',
  district: 'district',
  bible_college_campus: 'bible_college_campus',
  assembly: 'assembly',
  local: 'assembly',
  ministry: 'ministry',
  home_group: 'home_group',
  cell: 'cell',
};
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Plus,
  MapPin,
  Shield,
  MoreVertical,
  Pencil,
  Trash2,
  Globe,
  Phone,
  Mail,
  ExternalLink,
  Church,
  GripVertical,
  Image as ImageIcon,
  Upload,
  Loader2,
  X,
  Search,
  Filter,
  RotateCcw
} from 'lucide-react';
import GoogleMapPicker from '@/components/GoogleMapPicker';

const ChurchesManagement = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const isSuperAdmin = !!(user?.is_super_admin || user?.role === 'super_admin'
    || user?.permissions?.includes('*'));
  const [showDialog, setShowDialog] = useState(false);
  const [editingChurch, setEditingChurch] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [showBulkImport, setShowBulkImport] = useState(false);
  // Organisation hierarchy view (tree is the primary list; directory keeps the
  // logo/branding cards for per-church detail editing).
  const [viewMode, setViewMode] = useState('tree');
  const [showOrgForm, setShowOrgForm] = useState(false);
  const [orgEditing, setOrgEditing] = useState(null);
  const [orgDefaultParent, setOrgDefaultParent] = useState(null);
  const [showOrgWizard, setShowOrgWizard] = useState(false);
  const [setupOrg, setSetupOrg] = useState(null);
  const logoInputRef = useRef(null);
  
  // Filter state
  const [filters, setFilters] = useState({
    search: '',
    country: '',
    city: '',
    continent: '',
    region: '',
    district: '',
    is_global: null,
  });
  const [showFilters, setShowFilters] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    website_url: '',
    logo_url: '',
    location: '',
    address: '',
    // Geographic hierarchy
    continent: '',
    country: '',
    region: '',
    district: '',
    city: '',
    neighborhood: '',
    // Coordinates
    latitude: '',
    longitude: '',
    // Contact
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    description: '',
    is_global: true,
    currency: 'USD',
    // Org hierarchy (see /admin/churches/hierarchy for the ladder)
    level: '',
    parent_church_id: '',
  });

  // Handle logo file upload — goes through the shared media pipeline, which
  // downscales the picture and stores it in object storage.
  const handleLogoUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const asset = await uploadMedia(file, { purpose: 'logo' });
      setFormData(prev => ({ ...prev, logo_url: asset.url }));
      toast.success(`${asset.filename} uploaded`);
    } catch (error) {
      toast.error(error.message || 'That logo could not be uploaded');
    } finally {
      setUploading(false);
    }
  };

  // Fetch churches with filters
  const activeFilters = useMemo(() => {
    const params = {};
    if (filters.search) params.search = filters.search;
    if (filters.country) params.country = filters.country;
    if (filters.city) params.city = filters.city;
    if (filters.continent) params.continent = filters.continent;
    if (filters.region) params.region = filters.region;
    if (filters.district) params.district = filters.district;
    if (filters.is_global !== null) params.is_global = filters.is_global;
    return params;
  }, [filters]);

  const { data: churches, isLoading, error: fetchError } = useQuery({
    queryKey: ['admin-churches', activeFilters],
    queryFn: async () => {
      const r = await churchesAPI.adminList(activeFilters);
      // Ensure we always return an array
      return Array.isArray(r.data) ? r.data : [];
    }
  });

  // Fetch locations for filter dropdowns
  const { data: locations } = useQuery({
    queryKey: ['church-locations'],
    queryFn: async () => {
      try {
        const r = await churchesAPI.getLocations();
        const data = r.data || {};
        // Extract just the names from the location objects
        return {
          continents: (data.continents || []).map(c => c.name).filter(Boolean),
          countries: (data.countries || []).map(c => c.name).filter(Boolean),
          cities: (data.cities || []).map(c => c.name).filter(Boolean),
          regions: (data.regions || []).map(r => r.name).filter(Boolean),
          districts: (data.districts || []).map(d => d.name).filter(Boolean),
        };
      } catch {
        return { countries: [], cities: [], continents: [], regions: [], districts: [] };
      }
    }
  });

  // Reset filters
  const resetFilters = () => {
    setFilters({
      search: '',
      country: '',
      city: '',
      continent: '',
      region: '',
      district: '',
      is_global: null,
    });
  };

  // Hierarchy meta (levels ladder) + flat church list for parent picker in the
  // add/edit dialog. Keeps assignment inline so admins don't have to hop to
  // /admin/churches/hierarchy just to slot a new church into the tree.
  const { data: hxMeta } = useQuery({
    queryKey: ['hx-meta-levels'],
    queryFn: async () => {
      try {
        const r = await api.get('/churches-hx/meta/levels');
        return r.data;
      } catch {
        return { levels: [] };
      }
    },
    staleTime: 60_000 * 5,
  });

  // Check if any filters are active
  const hasActiveFilters = filters.search || filters.country || filters.city || filters.continent || filters.region || filters.district || filters.is_global !== null;

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (data) => churchesAPI.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries(['admin-churches']);
      toast.success('Church created successfully');
      handleCloseDialog();
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, 'Failed to create church'));
    }
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => churchesAPI.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['admin-churches']);
      toast.success('Church updated successfully');
      handleCloseDialog();
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, 'Failed to update church'));
    }
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id) => churchesAPI.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries(['admin-churches']);
      toast.success('Church deleted successfully');
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, 'Failed to delete church'));
    }
  });

  // Every create/edit goes through the one level-aware organisation form.
  const handleOpenCreate = () => {
    setOrgEditing(null);
    setShowOrgForm(true);
  };

  const handleOpenEdit = (church) => {
    setOrgEditing({
      id: church.id,
      name: church.name || '',
      code: church.code || '',
      organisation_type: church.organisation_type
        || LEVEL_TO_ORG_TYPE[(church.level || '').toLowerCase()] || '',
      parent_id: church.parent_church_id || '',
      status: church.status || 'active',
      description: church.description || '',
      country: church.country || '',
      countries: church.countries || [],
      region: church.region || church.state || '',
      district: church.district || '',
      city: church.city || '',
      address: church.address || '',
      currency: church.currency || 'USD',
      website_url: church.website_url || '',
      overseer_name: church.overseer_name || '',
      registration_number: church.registration_number || '',
      contact_name: church.contact_name || '',
      contact_email: church.contact_email || '',
      contact_phone: church.contact_phone || '',
      leader_member_id: church.leader_member_id || '',
      leader_name: church.leader_name || '',
      leader_title: church.leader_title || '',
      leader_photo_url: church.leader_photo_url || '',
    });
    setShowOrgForm(true);
  };

  const handleCloseDialog = () => {
    setShowDialog(false);
    setEditingChurch(null);
    setFormData({
      name: '',
      website_url: '',
      logo_url: '',
      location: '',
      address: '',
      continent: '',
      country: '',
      region: '',
      district: '',
      city: '',
      neighborhood: '',
      latitude: '',
      longitude: '',
      contact_name: '',
      contact_email: '',
      contact_phone: '',
      description: '',
      is_global: true,
      currency: 'USD',
      level: '',
      parent_church_id: '',
    });
  };

  const handleDelete = (church) => {
    if (window.confirm(`Are you sure you want to delete "${church.name}"?`)) {
      deleteMutation.mutate(church.id);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-10 w-32" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3].map(i => (
            <Skeleton key={i} className="h-48" />
          ))}
        </div>
      </div>
    );
  }

  if (fetchError) {
    return (
      <div className="p-6 text-center">
        <p className="text-red-500 mb-4">Failed to load churches</p>
        <Button onClick={() => queryClient.invalidateQueries(['admin-churches'])}>
          Try Again
        </Button>
      </div>
    );
  }

  // Ensure churches is always an array for safe rendering
  const churchesList = Array.isArray(churches) ? churches : [];
  // Groupings (HQ, country offices, provinces, districts) are navigation
  // records — they are listed for management but never counted as churches.
  const GROUPING_LEVELS = ['hq', 'headquarters', 'country', 'national', 'region', 'province', 'district', 'continent', 'zone'];
  const isGroupingRow = (c) => GROUPING_LEVELS.includes((c.level || '').toLowerCase()) || c.is_headquarters === true;
  const groupingCount = churchesList.filter(isGroupingRow).length;
  const congregationCount = churchesList.length - groupingCount;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Organisations</h2>
          <p className="text-sm text-slate-500 mt-1">
            Global Ministry → Country → Province → Assembly, with ministries, home groups and cells
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex rounded-md border border-slate-200 overflow-hidden mr-1">
            <button
              onClick={() => setViewMode('tree')}
              data-testid="view-mode-tree"
              className={`px-3 py-1.5 text-xs font-medium ${viewMode === 'tree' ? 'bg-slate-900 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
            >
              Tree
            </button>
            <button
              onClick={() => setViewMode('leaders')}
              data-testid="view-mode-leaders"
              className={`px-3 py-1.5 text-xs font-medium ${viewMode === 'leaders' ? 'bg-slate-900 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
            >
              Leaders
            </button>
            <button
              onClick={() => setViewMode('postings')}
              data-testid="view-mode-postings"
              className={`px-3 py-1.5 text-xs font-medium ${viewMode === 'postings' ? 'bg-slate-900 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
            >
              Postings
            </button>
            <button
              onClick={() => setViewMode('directory')}
              data-testid="view-mode-directory"
              className={`px-3 py-1.5 text-xs font-medium ${viewMode === 'directory' ? 'bg-slate-900 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
            >
              Directory
            </button>
          </div>
          <Button variant="outline" onClick={() => navigate('/admin/churches/hierarchy')} data-testid="open-hierarchy-btn">
            <Shield className="h-4 w-4 mr-2" />
            Hierarchy
          </Button>
          <Button variant="outline" onClick={() => navigate('/admin/churches/country-directory')} data-testid="open-country-directory-btn">
            <Globe className="h-4 w-4 mr-2" />
            Country Directory
          </Button>
          <Button variant="outline" onClick={() => navigate('/admin/affiliates')} data-testid="open-affiliated-ministries-btn">
            <Globe className="h-4 w-4 mr-2" />
            Affiliated Ministries
          </Button>
          <Button variant="outline" onClick={() => setShowBulkImport(true)} data-testid="bulk-import-btn">
            <Upload className="h-4 w-4 mr-2" />
            Bulk Import
          </Button>
          <Button
            variant="outline"
            onClick={() => { setOrgDefaultParent(null); setShowOrgWizard(true); }}
            data-testid="add-organisation-wizard-btn"
          >
            Guided setup
          </Button>
          <Button onClick={handleOpenCreate} data-testid="add-organisation-btn">
            <Plus className="h-4 w-4 mr-2" />
            Add New
          </Button>
        </div>
      </div>

      {viewMode === 'leaders' ? <LeadershipDirectory /> : null}

      {viewMode === 'postings' ? <ClergyTransfers /> : null}

      {viewMode === 'tree' ? (
        <div className="space-y-4">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Search organisations by name or code..."
              value={filters.search}
              onChange={(e) => setFilters((prev) => ({ ...prev, search: e.target.value }))}
              className="pl-10"
              data-testid="org-search-input"
            />
          </div>
        <OrganisationTree
          search={filters.search}
          onAddChild={(node) => { setOrgDefaultParent(node); setShowOrgWizard(true); }}
          onEdit={(node) => { setOrgDefaultParent(null); setOrgEditing(node); setShowOrgForm(true); }}
          onSetup={(node) => setSetupOrg(node)}
          onOpen={(node) => navigate(`/admin/churches/${node.id}`)}
        />
        </div>
      ) : null}

      {viewMode === 'directory' && (
      <>
      <DirectoryAuditPanel />
      <CountryCleanupPanel />
      <StaleRecordsPanel />

      {/* Search and Filters */}
      <Card>
        <CardContent className="pt-4 pb-4">
          <div className="flex flex-col gap-4">
            {/* Search bar */}
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Search by name, city, country, or address..."
                  value={filters.search}
                  onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                  className="pl-10"
                  data-testid="church-search-input"
                />
              </div>
              <Button
                variant="outline"
                onClick={() => setShowFilters(!showFilters)}
                className={showFilters ? 'bg-slate-100' : ''}
              >
                <Filter className="h-4 w-4 mr-2" />
                Filters
                {hasActiveFilters && (
                  <Badge variant="secondary" className="ml-2 h-5 w-5 p-0 flex items-center justify-center text-xs">
                    !
                  </Badge>
                )}
              </Button>
              {hasActiveFilters && (
                <Button variant="ghost" size="icon" onClick={resetFilters} title="Reset filters">
                  <RotateCcw className="h-4 w-4" />
                </Button>
              )}
            </div>

            {/* Filter dropdowns */}
            {showFilters && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 pt-2 border-t">
                {/* Continent */}
                <div className="space-y-1">
                  <Label className="text-xs text-slate-500">Continent</Label>
                  <Select
                    value={filters.continent || 'all'}
                    onValueChange={(value) => setFilters(prev => ({ ...prev, continent: value === 'all' ? '' : value }))}
                  >
                    <SelectTrigger data-testid="continent-filter">
                      <SelectValue placeholder="All Continents" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Continents</SelectItem>
                      {(locations?.continents || []).map(c => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Country */}
                <div className="space-y-1">
                  <Label className="text-xs text-slate-500">Country</Label>
                  <Select
                    value={filters.country || 'all'}
                    onValueChange={(value) => setFilters(prev => ({ ...prev, country: value === 'all' ? '' : value }))}
                  >
                    <SelectTrigger data-testid="country-filter">
                      <SelectValue placeholder="All Countries" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Countries</SelectItem>
                      {(locations?.countries || []).map(c => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Region/State */}
                <div className="space-y-1">
                  <Label className="text-xs text-slate-500">Region/State</Label>
                  <Select
                    value={filters.region || 'all'}
                    onValueChange={(value) => setFilters(prev => ({ ...prev, region: value === 'all' ? '' : value }))}
                  >
                    <SelectTrigger data-testid="region-filter">
                      <SelectValue placeholder="All Regions" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Regions</SelectItem>
                      {(locations?.regions || []).map(r => (
                        <SelectItem key={r} value={r}>{r}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* District */}
                <div className="space-y-1">
                  <Label className="text-xs text-slate-500">District</Label>
                  <Select
                    value={filters.district || 'all'}
                    onValueChange={(value) => setFilters(prev => ({ ...prev, district: value === 'all' ? '' : value }))}
                  >
                    <SelectTrigger data-testid="district-filter">
                      <SelectValue placeholder="All Districts" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Districts</SelectItem>
                      {(locations?.districts || []).map(d => (
                        <SelectItem key={d} value={d}>{d}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* City */}
                <div className="space-y-1">
                  <Label className="text-xs text-slate-500">City</Label>
                  <Select
                    value={filters.city || 'all'}
                    onValueChange={(value) => setFilters(prev => ({ ...prev, city: value === 'all' ? '' : value }))}
                  >
                    <SelectTrigger data-testid="city-filter">
                      <SelectValue placeholder="All Cities" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Cities</SelectItem>
                      {(locations?.cities || []).map(c => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Scope */}
                <div className="space-y-1">
                  <Label className="text-xs text-slate-500">Scope</Label>
                  <Select
                    value={filters.is_global === null ? 'all' : filters.is_global ? 'global' : 'event'}
                    onValueChange={(value) => setFilters(prev => ({ 
                      ...prev, 
                      is_global: value === 'all' ? null : value === 'global' 
                    }))}
                  >
                    <SelectTrigger data-testid="scope-filter">
                      <SelectValue placeholder="All Scope" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Scope</SelectItem>
                      <SelectItem value="global">Global Only</SelectItem>
                      <SelectItem value="event">Event-Specific Only</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Church className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold" data-testid="admin-congregation-count">{congregationCount}</p>
                <p className="text-sm text-slate-500">
                  Congregations
                  {groupingCount > 0 && (
                    <span className="block text-[11px] text-slate-400">
                      + {groupingCount} grouping{groupingCount === 1 ? '' : 's'} (not counted)
                    </span>
                  )}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <Globe className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {churchesList.filter(c => c.is_global).length}
                </p>
                <p className="text-sm text-slate-500">Global (Shown Everywhere)</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <MapPin className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {churchesList.filter(c => !c.is_global).length}
                </p>
                <p className="text-sm text-slate-500">Event-Specific</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Churches Grid */}
      {churchesList.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Church className="h-12 w-12 text-slate-400 mx-auto mb-4" />
            <p className="text-slate-600">No churches added yet</p>
            <p className="text-sm text-slate-500 mt-1">Add your first church to get started</p>
            <Button onClick={handleOpenCreate} className="mt-4">
              <Plus className="h-4 w-4 mr-2" />
              Add Church
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {churchesList.map(church => (
            <Card key={church.id} className="relative group" data-testid={`church-card-${church.id}`}>
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    {church.logo_url ? (
                      <img 
                        src={church.logo_url.startsWith('http') ? church.logo_url : `${process.env.REACT_APP_BACKEND_URL}${church.logo_url}`}
                        alt={church.name}
                        className="h-12 w-12 object-contain rounded-lg border"
                      />
                    ) : (
                      <div className="h-12 w-12 bg-slate-100 rounded-lg flex items-center justify-center">
                        <Church className="h-6 w-6 text-slate-400" />
                      </div>
                    )}
                    <div>
                      <CardTitle className="text-lg">{church.name}</CardTitle>
                      <div className="flex items-center gap-2 mt-1">
                        {church.is_global ? (
                          <Badge variant="secondary" className="text-xs">
                            <Globe className="h-3 w-3 mr-1" />
                            Global
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-xs">Event-Specific</Badge>
                        )}
                      </div>
                    </div>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8">
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => navigate(`/admin/churches/${church.id}`)} data-testid={`church-open-${church.id}`}>
                        <ExternalLink className="h-4 w-4 mr-2" />
                        Open church
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleOpenEdit(church)}>
                        <Pencil className="h-4 w-4 mr-2" />
                        Edit
                      </DropdownMenuItem>
                      {church.website_url && (
                        <DropdownMenuItem onClick={() => window.open(church.website_url, '_blank')}>
                          <ExternalLink className="h-4 w-4 mr-2" />
                          Visit Website
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuItem 
                        onClick={() => handleDelete(church)}
                        className="text-red-600"
                        disabled={!isSuperAdmin}
                        title={isSuperAdmin ? '' : 'Only a Super Admin can close a church down'}
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </CardHeader>
              <CardContent className="space-y-2 text-sm text-slate-600">
                {church.location && (
                  <div className="flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-slate-400" />
                    <span>{church.location}</span>
                  </div>
                )}
                {church.contact_phone && (
                  <div className="flex items-center gap-2">
                    <Phone className="h-4 w-4 text-slate-400" />
                    <span>{church.contact_phone}</span>
                  </div>
                )}
                {church.contact_email && (
                  <div className="flex items-center gap-2">
                    <Mail className="h-4 w-4 text-slate-400" />
                    <span>{church.contact_email}</span>
                  </div>
                )}
                {church.website_url && (
                  <div className="flex items-center gap-2">
                    <ExternalLink className="h-4 w-4 text-slate-400" />
                    <a 
                      href={church.website_url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline truncate"
                    >
                      {church.website_url.replace(/^https?:\/\//, '')}
                    </a>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
      </>
      )}

      {/* Create / edit runs through OrganisationFormDialog — one level-aware form. */}

      <BulkImportChurchesDialog open={showBulkImport} onOpenChange={setShowBulkImport} />

      <OrganisationFormDialog
        open={showOrgForm}
        onOpenChange={(v) => { setShowOrgForm(v); if (!v) { setOrgEditing(null); } }}
        editing={orgEditing}
      />

      <OrganisationWizard
        open={showOrgWizard}
        onOpenChange={(v) => { setShowOrgWizard(v); if (!v) setOrgDefaultParent(null); }}
        defaultParent={orgDefaultParent}
        onCreated={(org) => setSetupOrg(org)}
      />

      <OrganisationSetupDialog
        open={!!setupOrg}
        onOpenChange={(v) => { if (!v) setSetupOrg(null); }}
        org={setupOrg}
      />
    </div>
  );
};

export default ChurchesManagement;
